import {
    openOf,
    sumSiteCounts,
    type WarehouseFlowCounts,
    type WarehouseFlowSiteCounts,
    type WarehouseFlowView,
} from "./warehouseFlow.utils"

type StageNumbers = Partial<WarehouseFlowCounts["stages"]>

const countsOf = (
    stages: StageNumbers,
    behind: StageNumbers = {}
): WarehouseFlowCounts => ({
    stages: {
        new: 0,
        booked: 0,
        inTransit: 0,
        onHold: 0,
        delivered: 0,
        ...stages,
    },
    behind: {
        new: 0,
        booked: 0,
        inTransit: 0,
        onHold: 0,
        delivered: 0,
        ...behind,
    },
})

const FRESNO = "site:Fresno, CA"
const RENO = "site:Reno, NV"

const lane = ({
    siteId,
    counts,
    mode,
}: {
    siteId: string
    counts: WarehouseFlowCounts
    mode: WarehouseFlowSiteCounts["mode"]
}): WarehouseFlowSiteCounts => ({
    siteId,
    counts,
    mode,
    modeCounts: mode ? { [mode]: openOf(counts) } : {},
})

const party = ({
    name,
    place,
    bySite,
}: {
    name: string
    place: string
    bySite: WarehouseFlowSiteCounts[]
}) => ({
    name,
    place,
    counts: sumSiteCounts(bySite),
    bySite,
})

export const SAMPLE_WAREHOUSE_FLOW_VIEW: WarehouseFlowView = {
    shape: "outbound",
    loads: 83,
    totals: countsOf(
        { new: 7, booked: 8, inTransit: 7, delivered: 61 },
        { booked: 1, inTransit: 1 }
    ),
    sites: [
        {
            id: FRESNO,
            kind: "site",
            place: "Fresno, CA",
            placeCount: 0,
            counts: countsOf(
                { new: 5, booked: 6, inTransit: 5, delivered: 44 },
                { booked: 1, inTransit: 1 }
            ),
        },
        {
            id: RENO,
            kind: "site",
            place: "Reno, NV",
            placeCount: 0,
            counts: countsOf({
                new: 2,
                booked: 2,
                inTransit: 2,
                delivered: 17,
            }),
        },
    ],
    counterparties: [
        party({
            name: "Valley Fresh Markets",
            place: "Sacramento, CA",
            bySite: [
                lane({
                    siteId: FRESNO,
                    counts: countsOf({
                        new: 1,
                        booked: 2,
                        inTransit: 3,
                        delivered: 12,
                    }),
                    mode: "truckload",
                }),
            ],
        }),
        party({
            name: "Coastline Grocers DC",
            place: "Ontario, CA",
            bySite: [
                lane({
                    siteId: FRESNO,
                    counts: countsOf(
                        { new: 1, booked: 2, inTransit: 1, delivered: 9 },
                        { booked: 1 }
                    ),
                    mode: "truckload",
                }),
            ],
        }),
        party({
            name: "Bayside Foodservice",
            place: "Oakland, CA",
            bySite: [
                lane({
                    siteId: FRESNO,
                    counts: countsOf(
                        { new: 1, booked: 1, inTransit: 1, delivered: 8 },
                        { inTransit: 1 }
                    ),
                    mode: "ltl",
                }),
            ],
        }),
        party({
            name: "Sierra Cold Storage",
            place: "Reno, NV",
            bySite: [
                lane({
                    siteId: RENO,
                    counts: countsOf({
                        new: 1,
                        booked: 1,
                        inTransit: 1,
                        delivered: 10,
                    }),
                    mode: "truckload",
                }),
            ],
        }),
    ],
    others: {
        counterpartyCount: 5,
        bySite: [
            lane({
                siteId: FRESNO,
                counts: countsOf({ new: 2, booked: 1, delivered: 15 }),
                mode: "ltl",
            }),
            lane({
                siteId: RENO,
                counts: countsOf({
                    new: 1,
                    booked: 1,
                    inTransit: 1,
                    delivered: 7,
                }),
                mode: "truckload",
            }),
        ],
    },
    counterpartiesWithOpen: 9,
    isTruncated: false,
}
